import React, {useCallback, useEffect, useRef, useState} from 'react';
import {DialogContent, FormControl, Grid, Stack} from '@mui/material';
import {Modal, TextArea, Button} from '../../../../components';
import MemberShipPointsService from '../../../../services/MemberShipPoints/membership-points';
import {DataDetail} from '../../../../types/typeAllTransaction';
import success from '../../../../constants/success';
import useAuth from '../../../../hooks/useAuth';
import ApproveReceiptForm from './ApproveReceiptForm/ApproveReceiptForm';

// styles
import {
  StyledModalActions,
  StyledTitle,
  Typography,
} from './ModalApprovalReceipt.style';
import DeclineReceiptForm from './DeclineReceiptForm/DeclineReceiptForm';
import {formatDateTime} from '../../../../utils';
import {isEmpty, omit, reduce, some} from 'lodash';
import PointMultiplierEventService from '../../../../services/PointMultiplierEvent/pointMultiplierEvent.service';
import {
  getMatchingDataFromResponse,
  replaceDynamicContent,
} from '../../../../utils/helpers';
import {UPDATE_MEMBER_POINTS_FAILURE} from '../../../../constants/apiResponses';
import {
  DEFAULT_ERROR_INPUT_FIELDS,
  DEFAULT_INITIAL_VALUES,
  DEFAULT_KEYS_SELECTED,
  DEFAULT_PARTIAL_POINTS_CHECKBOXES,
} from '../../../../constants/transaction';

interface ModalApprovalReceipt {
  isModalApprovalReceipt?: boolean;
  typeModal?: string;
  title?: string;
  dataDetail?: DataDetail;
  onClose?: () => void;
  reloadPage?: () => void;
  receiptDetail?: any;
  memberPointsSummary?: any;
}

function ModalApprovalReceipt({
  isModalApprovalReceipt,
  onClose,
  title,
  typeModal,
  dataDetail,
  reloadPage,
  receiptDetail,
  memberPointsSummary,
}: ModalApprovalReceipt) {
  const {showNotification} = useAuth();

  // states
  const formatDateTimes = 'dd/MM/yyyy hh:mm';
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [yesNoAmend, setYesNoAmend] = useState<any>(undefined);
  const [errorMessageSelect, setErrorMessageSelect] = useState<string | null>(
    null
  );

  const [initialValues, setInitialValues] = useState(DEFAULT_INITIAL_VALUES);
  const [keysSelected, setKeysSelected] = useState<any>(DEFAULT_KEYS_SELECTED);
  const [rejectedFields, setRejectedFields] = useState<string[]>([]);
  const [receiptNameValue, setReceiptNameValue] = useState<string>('');
  const [receiptAddressValue, setReceiptAddressValue] = useState<number>('');
  const [receiptOperatingAreaValue, setReceiptOperatingAreaValue] =
    useState<string>('');
  const [
    selectedMerchantPointMultiplierEvents,
    setSelectedMerchantPointMultiplierEvents,
  ] = useState<any>([]);
  const [
    idGotSelectedMerchantPointMultiplierEvents,
    setIdGotSelectedMerchantPointMultiplierEvents,
  ] = useState<any>(null);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const formErrorRef = useRef<HTMLDivElement | null>(null);
  const [errorInputFields, setErrorInputFields] = useState<any>(
    DEFAULT_ERROR_INPUT_FIELDS
  );
  const [initialValuesDeclined, setInitialValuesDeclined] = useState({
    rejectReason: '',
  });
  const [partialPointsCheckboxes, setPartialPointsCheckboxes] = useState<any>(
    DEFAULT_PARTIAL_POINTS_CHECKBOXES
  );
  const [isSubmitButtonTemporaryDisabled, setIsSubmitButtonTemporaryDisabled] =
    useState<boolean>(false);

  const scrollToFormError = useCallback(() => {
    if (formErrorRef?.current) {
      formErrorRef?.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [formErrorRef.current]);

  useEffect(() => {
    if (formError) {
      scrollToFormError();
    }
  }, [formError]);

  const onClick = () => {
    const hasErrorValidationKeySelected = validationKeySelected();
    const hasErrorValidationFields = validationFields();
    const hasErrorValidationMissingInfo = validationMissingInfo();
    if (
      hasErrorValidationKeySelected === true ||
      hasErrorValidationFields === true ||
      hasErrorValidationMissingInfo === true
    ) {
      if (hasErrorValidationMissingInfo === true) {
        const updatedKeysSelected = getKeysSelectedData();
        setKeysSelected({
          ...keysSelected,
          ...updatedKeysSelected,
        });
      }
      return;
    }
    setIsLoading(true);
    new MemberShipPointsService()
      .update(`${dataDetail?.membershipId}/points/${dataDetail?.id}`, {
        processId: typeModal === 'approve' ? 1 : 3,
        // notes: notes,
        ...omit(initialValues, [
          'receiptAddress',
          'receiptName',
          'receiptOperatingArea',
          'receiptOperatingAreaValue',
        ]),
        receiptDateOfPurchase:
          initialValues.receiptDateOfPurchase === null
            ? undefined
            : formatDateTime(
                initialValues.receiptDateOfPurchase,
                formatDateTimes
              ),
        matchedMerchantID:
          initialValues.matchedMerchantID === ''
            ? undefined
            : initialValues.matchedMerchantID,
        // receiptAddress:
        //   initialValues.receiptAddress === ''
        //     ? undefined
        //     : initialValues.receiptAddress,
        rejectReason:
          initialValuesDeclined.rejectReason === ''
            ? undefined
            : initialValuesDeclined.rejectReason,
        rejectedFields: rejectedFields.length > 0 ? rejectedFields : undefined,
        reachedMaxDaily:
          partialPointsCheckboxes?.pointValue ??
          DEFAULT_PARTIAL_POINTS_CHECKBOXES?.pointValue,
        associatedPointReachedMaxDaily:
          partialPointsCheckboxes?.associatedPointValue ??
          DEFAULT_PARTIAL_POINTS_CHECKBOXES?.associatedPointValue,
      })
      .then(res => {
        if (res?.success) {
          onClose();
          reloadPage();
          setIsLoading(false);
          showNotification({
            message:
              typeModal === 'approve'
                ? success.APPROVAL_SUCCESS
                : success.DECLINED_SUCCESS,
          });
        } else {
          setIsSubmitButtonTemporaryDisabled(true);
          const matchedData = getMatchingDataFromResponse(
            UPDATE_MEMBER_POINTS_FAILURE,
            res
          );
          // If there is matchedData, that means there is config for inline error
          // Show inline error and hide loading
          if (!isEmpty(matchedData) && matchedData?.[0]) {
            setFormError(
              replaceDynamicContent(matchedData?.[0], [
                {
                  string: '{{dailyCapLimit}}',
                  replace: memberPointsSummary?.dailyCapLimit || '-',
                },
              ])
            );
            setIsLoading(false);
          } else {
            // If no matchedData, that means there is no config for inline error
            //  Close modal (if it's No amend), hide loading and show toast
            onCloseModal();
            setIsLoading(false);
            showNotification({
              message: res.errorMessage,
              variation: 'error',
            });
          }
        }
      });
  };

  const getMissingInfo = useCallback(() => {
    const notDeclinedModal = typeModal !== 'declined';
    if (notDeclinedModal) {
      const isMissingMerchantId =
        [null, undefined, '', 0, '0'].includes(dataDetail?.merchantId) &&
        initialValues?.merchantReceiptId === '';
      const isMissingSpentAmount =
        dataDetail?.spentAmount === null && initialValues?.receiptTotal === '';
      const isMissingPointValue =
        dataDetail?.pointValue === null && initialValues?.pointValue === '';
      return {
        ...(isMissingMerchantId ? {isMissingMerchantId} : {}),
        ...(isMissingSpentAmount ? {isMissingSpentAmount} : {}),
        ...(isMissingPointValue ? {isMissingPointValue} : {}),
      };
    }
    return {};
  }, [typeModal, dataDetail, initialValues]);

  const validationMissingInfo = (triggerActionSetFormError = true) => {
    const missingInfo = getMissingInfo();
    if (Object.values(missingInfo).length > 0) {
      if (triggerActionSetFormError === true) {
        setFormError(
          'This transaction has missing information. Please amend before approving.'
        );
      }
      return true;
    }
    if (triggerActionSetFormError === true) {
      setFormError(null);
    }
    return false;
  };

  const getKeysSelectedData = useCallback(() => {
    const missingInfo = getMissingInfo();
    if (Object.values(missingInfo).length > 0) {
      const updatedKeysSelected = {};
      Object.keys(missingInfo).forEach(key => {
        const map = {
          isMissingMerchantId: 'receiptPartnerAndAddress',
          isMissingSpentAmount: 'receiptTotal',
          isMissingPointValue: 'pointValue',
        };
        if (
          missingInfo[key] === true &&
          Object.keys(keysSelected).includes(map[key])
        ) {
          updatedKeysSelected[map[key]] = {
            value: missingInfo[key],
            type: 'force',
          };
        }
      });
      return updatedKeysSelected;
    }
    return {};
  }, [dataDetail, getMissingInfo]);

  const validationKeySelected = (triggerActionSetErrorMessageSelect = true) => {
    if (yesNoAmend === 'true') {
      if (
        !Object.values(keysSelected).find(
          (f: any) => f?.value === true || f === true
        )
      ) {
        if (triggerActionSetErrorMessageSelect === true) {
          setErrorMessageSelect(
            'Select at least one field you would like to amend, or select “No” to cancel the amendments.'
          );
        }
        return true;
      }
    }
    if (typeModal === 'declined') {
      if (initialValuesDeclined.rejectReason === '') {
        if (triggerActionSetErrorMessageSelect === true) {
          setErrorMessageSelect('Please select a reason for rejection.');
        }
        return true;
      }
    }
    if (triggerActionSetErrorMessageSelect === true) {
      setErrorMessageSelect(null);
    }
    return false;
  };

  const validationField = (fieldName: string, fieldValue: any = undefined) => {
    let isError = false;
    let errorMessage = '';
    const partialPointsCheckboxesObject: any = {};
    const localFieldValue = fieldValue;
    if (
      fieldName === 'oCRHistoryReceiptId' &&
      (keysSelected.oCRHistoryReceiptId?.value === true ||
        keysSelected.oCRHistoryReceiptId === true) &&
      localFieldValue === DEFAULT_INITIAL_VALUES.oCRHistoryReceiptId
    ) {
      errorMessage = 'Required';
      isError = true;
    }
    if (
      fieldName === 'receiptTotal' &&
      (keysSelected.receiptTotal?.value === true ||
        keysSelected.receiptTotal === true) &&
      localFieldValue === DEFAULT_INITIAL_VALUES.receiptTotal
    ) {
      errorMessage = 'Required';
      isError = true;
    }
    if (
      fieldName === 'receiptDateOfPurchase' &&
      (keysSelected.receiptDateOfPurchase?.value === true ||
        keysSelected.receiptDateOfPurchase === true) &&
      localFieldValue === DEFAULT_INITIAL_VALUES.receiptDateOfPurchase
    ) {
      errorMessage = 'Required';
      isError = true;
    }
    if (
      fieldName === 'receiptName' &&
      (keysSelected.receiptName?.value === true ||
        keysSelected.receiptName === true) &&
      localFieldValue === DEFAULT_INITIAL_VALUES.receiptName
    ) {
      errorMessage = 'Required';
      isError = true;
    }
    if (
      fieldName === 'merchantReceiptId' &&
      (keysSelected.receiptPartnerAndAddress?.value === true ||
        keysSelected.receiptPartnerAndAddress === true) &&
      localFieldValue === DEFAULT_INITIAL_VALUES.merchantReceiptId
    ) {
      errorMessage = 'Required';
      isError = true;
    }
    if (
      fieldName === 'matchedMerchantID' &&
      (keysSelected.receiptPartnerAndAddress?.value === true ||
        keysSelected.receiptPartnerAndAddress === true) &&
      localFieldValue === DEFAULT_INITIAL_VALUES.matchedMerchantID
    ) {
      errorMessage = 'Required';
      isError = true;
    }
    if (
      fieldName === 'receiptAddress' &&
      (keysSelected.receiptPartnerAndAddress?.value === true ||
        keysSelected.receiptPartnerAndAddress === true) &&
      localFieldValue === DEFAULT_INITIAL_VALUES.receiptAddress
    ) {
      errorMessage = 'Required';
      isError = true;
    }
    const getDataForCheckingPoint = () => {
      const pointValue = Number(initialValues.pointValue);
      const associatedPointValue = Number(initialValues.associatedPointValue);
      const newInputtedTotalAmount =
        keysSelected.receiptTotal?.value === true ||
        keysSelected.receiptTotal === true
          ? parseInt(initialValues.receiptTotal, 10)
          : null;
      const parsedReceiptTotal = parseInt(receiptDetail?.receiptTotal, 10);
      const receiptTotal =
        newInputtedTotalAmount !== null && !isNaN(newInputtedTotalAmount)
          ? newInputtedTotalAmount
          : parsedReceiptTotal;
      const approvedPoints = Number(memberPointsSummary?.approvedPoints) || 0;
      const dailyCapLimit = Number(memberPointsSummary?.dailyCapLimit);
      const pointsMultiplier = Number(dataDetail?.pointsMultiplier);
      return {
        pointValue,
        associatedPointValue,
        receiptTotal,
        approvedPoints,
        dailyCapLimit,
        pointsMultiplier,
      };
    };
    if (
      fieldName === 'pointValue' &&
      (keysSelected.pointValue?.value === true ||
        keysSelected.pointValue === true)
    ) {
      const {pointValue, receiptTotal, approvedPoints, dailyCapLimit} =
        getDataForCheckingPoint();
      if (
        (keysSelected.pointValue?.value === true ||
          keysSelected.pointValue === true) &&
        localFieldValue === DEFAULT_INITIAL_VALUES.pointValue
      ) {
        errorMessage = 'Required';
        isError = true;
        partialPointsCheckboxesObject.pointValue = false;
      } else if (
        localFieldValue &&
        Number(localFieldValue) > 0 &&
        memberPointsSummary?.minSpendAmount &&
        (isNaN(receiptTotal) ||
          receiptTotal < memberPointsSummary?.minSpendAmount)
      ) {
        errorMessage = `Points cannot be earned as transaction is less than $${memberPointsSummary?.minSpendAmount}.`;
        isError = true;
        partialPointsCheckboxesObject.pointValue = false;
      } else {
        if (
          Number(localFieldValue) >= 0 &&
          receiptTotal >= (memberPointsSummary?.minSpendAmount || 0) &&
          pointValue < receiptTotal
        ) {
          partialPointsCheckboxesObject.pointValue = true;
        } else {
          partialPointsCheckboxesObject.pointValue = false;
        }

        if (dailyCapLimit && pointValue + approvedPoints > dailyCapLimit) {
          errorMessage = 'Exceeds maximum daily points of 600.';
          isError = true;
        } else if (pointValue > receiptTotal) {
          errorMessage = 'Points cannot be more than the amount spent.';
          isError = true;
        }
      }
    }
    if (
      fieldName === 'associatedPointValue' &&
      (keysSelected.pointValue?.value === true ||
        keysSelected.pointValue === true)
    ) {
      const {
        pointValue,
        associatedPointValue,
        receiptTotal,
        approvedPoints,
        dailyCapLimit,
        pointsMultiplier,
      } = getDataForCheckingPoint();
      if (
        Number(localFieldValue) >= 0 &&
        receiptTotal >= (memberPointsSummary?.minSpendAmount || 0) &&
        associatedPointValue < receiptTotal
      ) {
        partialPointsCheckboxesObject.associatedPointValue = true;
      } else {
        partialPointsCheckboxesObject.associatedPointValue = false;
      }
      if (
        dailyCapLimit &&
        Number(localFieldValue) > 0 &&
        pointValue + associatedPointValue + approvedPoints > dailyCapLimit
      ) {
        errorMessage = 'Exceeds maximum daily points of 600.';
        isError = true;
      } else if (
        pointsMultiplier >= 2 &&
        associatedPointValue * (pointsMultiplier - 1) > pointValue
      ) {
        errorMessage = `Bonus points must be the same as main transaction as this is a ${pointsMultiplier}X points transaction.`;
        isError = true;
      } else if (
        pointValue &&
        associatedPointValue &&
        pointValue + associatedPointValue <= receiptTotal
      ) {
        errorMessage = `All remaining ${
          pointValue + associatedPointValue
        } points must be assigned to the main transaction.`;
        isError = true;
      }
    }
    const errorsObject = {
      [fieldName]: errorMessage,
    };
    return {
      isError,
      errorsObject,
      partialPointsCheckboxesObject,
    };
  };

  const validationSpecialFields = (fieldNames: string[]): any => {
    const validation = fieldNames.reduce((acc, fieldName) => {
      const validateResult = validationField(
        fieldName,
        initialValues[fieldName]
      );
      return {
        ...acc,
        [fieldName]: validateResult,
      };
    }, {});
    return validation;
  };

  const validationFields = (
    triggerActionSetErrorInputFields = true,
    triggerActionSetPartialPointsCheckboxes = true
  ) => {
    const validationAllFields = validationSpecialFields(
      Object.keys(initialValues)
    );
    const errorsObject = reduce(
      validationAllFields,
      (acc, fieldName) => {
        return {
          ...acc,
          ...validationAllFields?.[fieldName]?.errorsObject,
        };
      },
      {}
    );
    const partialPointsCheckboxesObject = reduce(
      validationAllFields,
      (acc, fieldName) => {
        return {
          ...acc,
          ...validationAllFields?.[fieldName]?.partialPointsCheckboxesObject,
        };
      },
      {}
    );
    const isError = some(validationAllFields, {isError: true});
    if (triggerActionSetErrorInputFields === true) {
      setErrorInputFields({
        ...errorInputFields,
        ...errorsObject,
      });
    }
    if (triggerActionSetPartialPointsCheckboxes === true) {
      setPartialPointsCheckboxes(partialPointsCheckboxesObject);
    }
    return isError;
  };

  const resetForm = () => {
    setInitialValuesDeclined({rejectReason: ''});
    setInitialValues(DEFAULT_INITIAL_VALUES);
    setKeysSelected(DEFAULT_KEYS_SELECTED);
    setRejectedFields([]);
    setErrorMessageSelect(null);
    setReceiptNameValue('');
    setReceiptAddressValue('');
    setReceiptOperatingAreaValue('');
    setErrorInputFields(DEFAULT_ERROR_INPUT_FIELDS);
    setFormError(undefined);
    setPartialPointsCheckboxes(DEFAULT_PARTIAL_POINTS_CHECKBOXES);
    setIsLoading(false);
  };

  useEffect(() => {
    if (Object.values(keysSelected).find(f => f === true)) {
      setErrorMessageSelect(null);
    }
  }, [keysSelected]);

  useEffect(() => {
    resetForm();
    if (yesNoAmend === 'false') {
      validationMissingInfo();
    }
  }, [yesNoAmend]);

  useEffect(() => {
    const hasErrorValidationMissingInfo = validationMissingInfo(false);
    if (!hasErrorValidationMissingInfo && !!formError) {
      setFormError(undefined);
    }
    setIsSubmitButtonTemporaryDisabled(false);
  }, [initialValues, yesNoAmend]);

  useEffect(() => {
    // Check for special fields when admin changes:
    // - New total amount
    // - Adjusted points earned
    // - Bonus adjusted points earned
    // Run validation to check and show corresponding errors/messages
    const errorInputFieldsObject: any = {};
    const partialPointsCheckboxesObject: any = {};
    if (
      keysSelected.receiptTotal === true &&
      initialValues.receiptTotal !== ''
    ) {
      const validateReceiptTotal = validationField(
        'receiptTotal',
        initialValues.receiptTotal
      );
      errorInputFieldsObject.receiptTotal =
        validateReceiptTotal.errorsObject.receiptTotal;
      partialPointsCheckboxesObject.receiptTotal =
        validateReceiptTotal.partialPointsCheckboxesObject.receiptTotal;
    } else {
      errorInputFieldsObject.receiptTotal = '';
      partialPointsCheckboxesObject.receiptTotal = false;
    }

    if (keysSelected.pointValue === true && initialValues.pointValue !== '') {
      const validatePointValue = validationField(
        'pointValue',
        initialValues.pointValue
      );
      errorInputFieldsObject.pointValue =
        validatePointValue.errorsObject.pointValue;
      partialPointsCheckboxesObject.pointValue =
        validatePointValue.partialPointsCheckboxesObject.pointValue;
    } else {
      errorInputFieldsObject.pointValue = '';
      partialPointsCheckboxesObject.pointValue = false;
    }

    if (
      keysSelected.pointValue === true &&
      initialValues.associatedPointValue !== ''
    ) {
      const validateAssociatedPointValue = validationField(
        'associatedPointValue',
        initialValues.associatedPointValue
      );
      errorInputFieldsObject.associatedPointValue =
        validateAssociatedPointValue.errorsObject.associatedPointValue;
      partialPointsCheckboxesObject.associatedPointValue =
        validateAssociatedPointValue.partialPointsCheckboxesObject.associatedPointValue;
    } else {
      errorInputFieldsObject.associatedPointValue = '';
      partialPointsCheckboxesObject.associatedPointValue = false;
    }

    if (!isEmpty(errorInputFieldsObject)) {
      setErrorInputFields({
        ...errorInputFields,
        ...errorInputFieldsObject,
      });
    }

    if (!isEmpty(partialPointsCheckboxesObject)) {
      setPartialPointsCheckboxes({
        ...partialPointsCheckboxes,
        ...partialPointsCheckboxesObject,
      });
    }
  }, [initialValues, keysSelected]);

  useEffect(() => {
    if (
      initialValues?.matchedMerchantID &&
      initialValues?.matchedMerchantID !==
        idGotSelectedMerchantPointMultiplierEvents
    ) {
      new PointMultiplierEventService()
        .get(null, {
          status_eq: 1,
          merchantId_eq: initialValues.matchedMerchantID,
          dateTime_eq: dataDetail?.createdDateTime,
        })
        .then(res => {
          setSelectedMerchantPointMultiplierEvents(res?.data?.data?.items);
          setIdGotSelectedMerchantPointMultiplierEvents(
            initialValues?.matchedMerchantID
          );
        });
    }
  }, [initialValues, idGotSelectedMerchantPointMultiplierEvents]);

  useEffect(() => {
    setErrorMessageSelect(null);
  }, [initialValuesDeclined]);

  const shouldDisableSubmitButton = () => {
    const isApproveModalWithoutYesNoAmendSelected =
      typeModal === 'approve' && yesNoAmend === undefined;
    const hasErrorValidationKeySelected = validationKeySelected(false);
    const hasErrorValidationFields = validationFields(false, false);
    const hasErrorValidationMissingInfo =
      typeModal === 'approve' &&
      yesNoAmend === 'false' &&
      validationMissingInfo(false);
    return (
      isApproveModalWithoutYesNoAmendSelected ||
      hasErrorValidationKeySelected === true ||
      hasErrorValidationFields === true ||
      isSubmitButtonTemporaryDisabled === true ||
      hasErrorValidationMissingInfo === true
    );
  };

  const onCloseModal = () => {
    onClose();
    resetForm();
  };

  return (
    <Modal
      visible={isModalApprovalReceipt}
      onClose={onCloseModal}
      maxWidth="sm"
      iconVariant={typeModal === 'approve' ? 'success' : 'alert'}
    >
      <DialogContent
        sx={{
          padding: '0px 64px',
        }}
      >
        <Grid container justifyContent="space-around">
          <Grid item mt={3} xs={12} md={12} textAlign="center">
            <StyledTitle className={`type type--${typeModal}`}>
              {title}
            </StyledTitle>
          </Grid>
        </Grid>
        {typeModal === 'approve' && (
          <ApproveReceiptForm
            transactionDetail={dataDetail}
            setInitialValues={setInitialValues}
            initialValues={initialValues}
            setYesNoAmend={setYesNoAmend}
            yesNoAmend={yesNoAmend}
            setKeysSelected={setKeysSelected}
            keysSelected={keysSelected}
            setReceiptOperatingAreaValue={setReceiptOperatingAreaValue}
            receiptOperatingAreaValue={receiptOperatingAreaValue}
            setReceiptNameValue={setReceiptNameValue}
            receiptNameValue={receiptNameValue}
            setReceiptAddressValue={setReceiptAddressValue}
            receiptAddressValue={receiptAddressValue}
            errorInputFields={errorInputFields}
            setErrorInputFields={setErrorInputFields}
            formError={formError}
            validationField={validationField}
            receiptDetail={receiptDetail}
            memberPointsSummary={memberPointsSummary}
            partialPointsCheckboxes={partialPointsCheckboxes}
            setPartialPointsCheckboxes={setPartialPointsCheckboxes}
            selectedMerchantPointMultiplierEvents={
              selectedMerchantPointMultiplierEvents
            }
            formErrorRef={formErrorRef}
          />
        )}
        {typeModal === 'declined' && (
          <DeclineReceiptForm
            transactionDetail={dataDetail}
            setInitialValues={setInitialValuesDeclined}
            initialValues={initialValuesDeclined}
            rejectedFields={rejectedFields}
            setRejectedFields={setRejectedFields}
          />
        )}
        {errorMessageSelect !== null && (
          <span style={{display: 'block'}} className="mandatory-label">
            {errorMessageSelect}
          </span>
        )}
      </DialogContent>
      <StyledModalActions>
        <Grid container>
          <Grid item xs={12}>
            <Stack spacing={2} direction="row" justifyContent="center">
              <Button
                variant="contained"
                onClick={onClick}
                width="125px"
                loading={isLoading}
                color={typeModal === 'approve' ? 'success' : 'error'}
                disabled={shouldDisableSubmitButton()}
              >
                {typeModal === 'approve' ? 'Approve' : 'Decline'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  onCloseModal();
                  setYesNoAmend(undefined);
                }}
                width="125px"
                loading={isLoading}
              >
                Cancel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </StyledModalActions>
    </Modal>
  );
}
export default ModalApprovalReceipt;
